<template>
  <!-- The highlighted pair on the table photo. The square matches the social sheets,
       and the face rises through its top edge the way the collection's pair does. -->
  <div class="shuffle-slot" aria-hidden="true">
    <div class="shuffle-rise">
      <div class="shuffle-pair">
        <img class="pair-photo" :src="tablePhoto" alt="" />
      </div>
      <div class="pair-stack">
        <div
          v-for="layer in layers"
          :key="layer.id"
          :ref="(el) => bindLayer(layer.id, el)"
          class="pair-layer"
          :class="{ 'is-entering': layer.id === enteringId }"
        >
          <span class="pair-face">
            <img :src="layer.face" alt="" />
          </span>
          <span class="pair-rank">
            <span
              v-for="(piece, index) in piecesFor(layer.position, layer.white)"
              :key="index"
              class="pair-square"
              :class="(index % 2 === 0) === layer.white ? 'is-dark' : 'is-light'"
            >
              <GamChessboardPiece :piece="piece" />
            </span>
          </span>
          <p class="pair-caption">
            <span class="pair-caption-line">
              <span class="is-unique">#{{ layer.position }}</span>
              <span aria-hidden="true">・</span>
              <span class="is-unique">{{ layer.name }}</span>
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import GamChessboardPiece from '@/views/components/gam-chessboard/GamChessboardPiece.vue';
import { backRankFromNumber } from '@/views/main/handshakes/utils/handshake-game';
import { nextTick, onBeforeUnmount, ref, watch, type ComponentPublicInstance } from 'vue';

const props = defineProps<{
  /** The Chess960 number this pair was played on. */
  positionIndex: number;
  /** The player, the same faces the rest of the landing already uses. */
  faceSrc: string;
  /** Their handle, beside the number. */
  playerName: string;
  /** The player's own colour. Black's rank runs h to a. */
  visitorWhite: boolean;
}>();

/** The table that used to sit under the recommendation. */
const tablePhoto = new URL('../../ui/images/auth/position-54.jpg', import.meta.url).href;

interface PairLayer {
  id: number;
  position: number;
  face: string;
  name: string;
  white: boolean;
}

/** The pair on the card, and the next one dissolving over it. */
const layers = ref<PairLayer[]>([]);
/** The incoming pair starts clear, so the one underneath never drops out. */
const enteringId = ref<number | null>(null);
let nextLayerId = 0;
let pairFade: Animation | null = null;
let captionFade: Animation | null = null;
const layerEls = new Map<number, HTMLElement>();

const bindLayer = (id: number, el: Element | ComponentPublicInstance | null) => {
  if (el instanceof HTMLElement) layerEls.set(id, el);
  else layerEls.delete(id);
};

const PAIR_FADE_MS = 420;

const makeLayer = (position: number, face: string, name: string, white: boolean): PairLayer => ({
  id: nextLayerId++,
  position,
  face,
  name,
  white,
});

layers.value = [makeLayer(props.positionIndex, props.faceSrc, props.playerName, props.visitorWhite)];

/** White's back rank from the a-file, or black's from the h-file when the player has black. */
const piecesFor = (position: number, white: boolean) => {
  const rank = backRankFromNumber(position);

  return white ? rank : [...rank].reverse().map((piece) => piece.toLowerCase());
};

/**
 * The next pair fades in over the current one. Fading the only copy out lets the
 * table photo show through the board, which reads as the whole pair blinking away.
 */
watch(
  () => [props.positionIndex, props.faceSrc, props.playerName, props.visitorWhite] as const,
  async ([position, face, name, white]) => {
    const current = layers.value[layers.value.length - 1];
    if (!current || pairFade) return;
    if (current.position === position && current.face === face && current.name === name && current.white === white) {
      return;
    }

    const incoming = makeLayer(position, face, name, white);
    enteringId.value = incoming.id;
    layers.value = [current, incoming];
    await nextTick();

    const mark = layerEls.get(incoming.id);
    if (!mark) {
      enteringId.value = null;
      layers.value = [incoming];
      return;
    }

    const fade = { duration: PAIR_FADE_MS, easing: 'ease-in-out', fill: 'forwards' as const };
    const fadeIn = mark.animate([{ opacity: 0 }, { opacity: 1 }], fade);
    pairFade = fadeIn;
    // The line has no fill behind it, so the old number and name stay readable until this copy leaves with the pair.
    const outgoingCaption = layerEls.get(current.id)?.querySelector<HTMLElement>('.pair-caption');
    captionFade = outgoingCaption?.animate([{ opacity: 1 }, { opacity: 0 }], fade) ?? null;
    try {
      await fadeIn.finished;
      if (pairFade !== fadeIn) return;
      mark.style.opacity = '1';
      enteringId.value = null;
      await nextTick();
      if (pairFade !== fadeIn) return;
      fadeIn.cancel();
      captionFade?.cancel();
      mark.style.opacity = '';
      layers.value = [incoming];
      pairFade = null;
      captionFade = null;
    } catch {
      if (pairFade === fadeIn) pairFade = null;
      captionFade = null;
    }
  },
);

onBeforeUnmount(() => {
  pairFade?.cancel();
  captionFade?.cancel();
  pairFade = null;
  captionFade = null;
});
</script>

<style lang="scss" scoped>
/* The slot is the size the face is measured against. The rise is a child, so that measure is its container. */
.shuffle-slot {
  width: 100%;
  container-type: inline-size;
}

/* Room above the square for half the face, the same rise the collection card gives its pair. */
.shuffle-rise {
  --pair-face: calc((100cqi - 2 * var(--spacing-h)) / 4);
  position: relative;
  padding-top: calc(var(--pair-face) * 0.5);
}

/* The social sheets' square: same width, same rounding, the table photo where their board is. */
.shuffle-pair {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: calc(36 / 420 * 100%);
  background: var(--color-dark-700);
  box-shadow:
    0 0 0 0.5px rgba(255, 255, 255, 0.08),
    0 2px 4px rgba(0, 0, 0, 0.45),
    0 18px 40px rgba(0, 0, 0, 0.55);
}

/* The pair sits on the top edge: the face half above the photo, the rank just inside it. */
.pair-stack {
  position: absolute;
  top: 0;
  right: var(--spacing-h);
  left: var(--spacing-h);
  z-index: 1;
  display: grid;
}

/* Two pairs share one cell. The incoming one fades in over the one already there.
   Isolate each layer, or the old rank (it sits above the face) stays on top and the face changes first. */
.pair-layer {
  grid-area: 1 / 1;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  isolation: isolate;

  &.is-entering {
    z-index: 1;
    opacity: 0;
  }
}

.pair-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: grayscale(1);
  /* Strong enough to read as the table, quiet enough that the pair stays on top of it. */
  opacity: 0.55;
  pointer-events: none;
}

/* The highlighted pair's own ratio: the face is two squares of the rank, on a rank as wide as the boards. */
.pair-face {
  z-index: 0;
  position: relative;
  display: grid;
  place-items: center;
  width: var(--pair-face);
  height: var(--pair-face);
  /* Only the empty rim tucks under the rank. A percentage here is of the pair, not the face. */
  margin-bottom: calc(var(--pair-face) * -0.12);
  overflow: hidden;
  border-radius: 50%;
  background: var(--color-dark-500);
  box-shadow:
    0 0 0 3px var(--color-dark-600),
    0 8px 20px rgba(0, 0, 0, 0.35);

  /* The drawings fill their square, and a square the size of this circle would be cut at the ring. */
  img {
    width: 66%;
    height: 66%;
    object-fit: contain;
  }
}

.pair-rank {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  width: 100%;
  aspect-ratio: 8 / 1;
  overflow: hidden;
  border-radius: var(--radius-x-small);
}

.pair-square {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &.is-light {
    background: #f0d9b5;
  }

  &.is-dark {
    background: #b58863;
  }
}

.pair-caption {
  position: relative;
  z-index: 1;
  display: grid;
  margin: 2cqi 0 0;
  font-size: 4cqi;
  font-weight: 500;
  letter-spacing: 0.05em;
  color: var(--color-white-50);
}

.pair-caption-line {
  grid-area: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35em;

  .is-unique {
    color: var(--color-pink);
  }
}
</style>
